// ─────────────────────────────────────────────────────────────────────────────
// LandingPage, Wavelength's public front page.
//
// Structure follows the reference layout the brand is being aligned to: an inset
// full-bleed hero panel with the nav floating on it, then alternating white and
// grey sections, left-aligned headings that end in a period, oversized card
// radii, one dark panel in the middle, a spec panel, a stat grid with an
// interactive calculator, a compact plan card, an FAQ accordion and a dark
// closing CTA.
//
// The product has no photography or video yet, so every image and video slot is
// a <MediaSlot>: a flat grey panel captioned with the asset that belongs there.
//
// Nothing here names a specific customer. The page describes the platform and
// the category, and it names the point-of-sale systems it sits on top of.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    BTN_PRIMARY,
    BTN_SECONDARY,
    BTN_ON_DARK,
    BTN_ON_DARK_SOFT,
    BTN_MD,
    NUMS,
    Icon,
    Container,
    Section,
    Heading,
    CardTitle,
    Lead,
    Body,
    KeyDark,
    Pill,
    FloatingChip,
    Badge,
    MediaSlot,
    SectionHead,
    FeatureRow,
    FeatureGrid,
    MediaCardGrid,
    StatBand,
    Steps,
    Accordion,
    CTASection,
    HeroPanel,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── The nine things Wavelength does, in the operator's own words ──────────────
const MODULES = [
    { icon: 'clock', title: 'Close the day', body: 'One screen per location per day. Register totals, deposits, variances and sign-off, with the day locked once it is closed.' },
    { icon: 'cash', title: 'Prove the cash', body: 'Blind drawer counts, safe counts and deposit slips. Every hand that touched the money is on the record.' },
    { icon: 'bank', title: 'Reconcile to the bank', body: 'Import the bank statement and match it against sales, payouts and deposits. What has not cleared is the whole list.' },
    { icon: 'wallet', title: 'Settle every tender', body: 'Card, cash, EBT and SNAP, gift certificates and online payments, each traced from the order to the payout.' },
    { icon: 'chart', title: 'Know what sells', body: 'Sales, products, customers, peak hours and campaigns across every location and your online store, side by side.' },
    { icon: 'boxes', title: 'Track every case', body: 'An append-only movement ledger. On hand is derived from what actually moved, never typed over.' },
    { icon: 'truck', title: 'Purchase and receive', body: 'Reorder boards that prefill purchase orders, and an iPad that signs off the delivery at the back door.' },
    { icon: 'printer', title: 'Produce and label', body: 'Scale labels, shelf labels and barcode formats, with yield and waste tracked from raw material to finished pack.' },
    { icon: 'receipt', title: 'Bill wholesale accounts', body: 'Invoices raised automatically, price levels by account, credit memos on the ledger and receivables aged.' },
];

const EXTRAS = [
    { icon: 'alert', title: 'Exceptions queue', body: 'Everything that did not tie out, in one worklist with the location, the source and the amount.' },
    { icon: 'lock', title: 'Audit log', body: 'Who changed what, when, and from which device. Nothing is edited silently.' },
    { icon: 'shield', title: 'Roles and permissions', body: 'Eight role presets across every module, so a manager sees their store and nothing else.' },
    { icon: 'phone', title: 'Device fleet', body: 'Every register, scale and iPad in one list, with health, version and last check-in.' },
    { icon: 'users', title: 'Staff and PINs', body: 'Staff records, per-app access, single-use codes and the location each person works.' },
    { icon: 'search', title: 'Ask it anything', body: 'A built-in assistant that answers questions about your own numbers and shows the work.' },
    { icon: 'pin', title: 'Delivery tracking', body: 'Driver location on the map for local delivery, refreshed while the run is out.' },
    { icon: 'receipt', title: 'QuickBooks export', body: 'The closed day exported in the shape your bookkeeper already reconciles.' },
    { icon: 'bolt', title: 'Notifications', body: 'Order and packing alerts pushed to the floor, marked read when they are handled.' },
    { icon: 'tag', title: 'Coupons and certificates', body: 'Issue, redeem and carry the outstanding liability instead of guessing at it.' },
    { icon: 'layers', title: 'Multiple workspaces', body: 'Run more than one business from one login, each with its own brand, data and staff.' },
    { icon: 'sparkle', title: 'Export anything', body: 'Every table exports to CSV, Excel or a branded PDF from the page you are looking at.' },
];

const POS_LOGOS = ['Toast', 'Clover', 'Square', 'Lightspeed', 'Shopify', 'WooCommerce', 'QuickBooks'];

const FAQ = [
    {
        q: 'Do I have to replace my point-of-sale system?',
        a: 'No. Wavelength is the back office, not the register. It reads from what you already run, in store and online, and gives you the close, the cash controls, the reconciliation and the reporting on top of it. Your staff keep ringing sales the way they do today.',
    },
    {
        q: 'What does Wavelength actually reconcile?',
        a: 'Three sources against each other: what the registers and the online store recorded as sales, what the payment processor says it paid out, and what the bank statement shows arriving. Anything that does not line up lands in the exceptions queue with the location, the date and the amount.',
    },
    {
        q: 'How is cash handled?',
        a: 'Drawer counts are blind, so the person counting does not see the expected figure. Safe counts, deposits and the day sheet are recorded per location, and the variance is attributed to the person and the drawer rather than lost in a total.',
    },
    {
        q: 'Can it run more than one location?',
        a: 'That is what it is for. Every screen filters by location, and the day closes per location while the numbers roll up. Adding a store does not mean adding another login or another spreadsheet.',
    },
    {
        q: 'Does it work for an online store as well as the counter?',
        a: 'Yes. Orders from the website sit next to counter sales in the same tables, with the same statuses, the same tenders and the same reconciliation. Campaign traffic is matched to the orders it actually produced.',
    },
    {
        q: 'What about weighed items, production and labels?',
        a: 'Weighed lines carry the weight and the price per pound through to the receipt, the report and the export. Production runs are tracked from raw material to finished pack with yield and waste, and the scales print the label formats you define.',
    },
    {
        q: 'How long does it take to get running?',
        a: 'Connect a store, import a bank statement and close one day. Most of the setup is picking your locations, your registers and who is allowed to see what. History backfills behind you while you work.',
    },
    {
        q: 'Where does the data live?',
        a: 'In your own workspace on Amazon Web Services, encrypted in transit and at rest, with role-based access and an audit trail on every change. See the security page for the detail.',
    },
];

// ── The unreconciled-day calculator, an interactive echo of the stat section ───
function ExposureCalculator() {
    const [locations, setLocations] = useState(3);
    const [dailyCash, setDailyCash] = useState(2400);
    const [driftPct, setDriftPct] = useState(1.5);

    const { yearly, hours } = useMemo(() => {
        const perDay = locations * dailyCash * (driftPct / 100);
        return {
            yearly: Math.round(perDay * 6 * 52),
            hours: Math.round(locations * 45 * 12) / 60,
        };
    }, [locations, dailyCash, driftPct]);

    const field = 'h-11 w-full rounded-full border border-[rgba(10,10,10,0.12)] bg-white px-5 text-[15px] font-medium text-[#0A0A0A] outline-none transition-colors focus:border-[rgba(10,10,10,0.35)]';
    const label = 'text-[13px] font-medium text-[#6B6B6B]';

    return (
        <div className="grid grid-cols-1 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white lg:grid-cols-[1.15fr_1fr]">
            <div className="flex flex-col gap-5 border-b border-[rgba(10,10,10,0.08)] p-6 sm:p-8 lg:border-b-0 lg:border-r">
                <div>
                    <CardTitle>What an unreconciled day costs you</CardTitle>
                    <Body className="mt-2">Rough arithmetic on your own numbers. Drift is the share of cash that goes unexplained on an average trading day.</Body>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <label className="flex flex-col gap-2">
                        <span className={label}>Locations</span>
                        <input type="number" min="1" max="99" value={locations} onChange={(e) => setLocations(Math.max(1, Number(e.target.value) || 1))} className={field} />
                    </label>
                    <label className="flex flex-col gap-2">
                        <span className={label}>Cash per day</span>
                        <input type="number" min="0" step="100" value={dailyCash} onChange={(e) => setDailyCash(Math.max(0, Number(e.target.value) || 0))} className={field} />
                    </label>
                    <label className="flex flex-col gap-2">
                        <span className={label}>Drift %</span>
                        <input type="number" min="0" max="20" step="0.1" value={driftPct} onChange={(e) => setDriftPct(Math.max(0, Number(e.target.value) || 0))} className={field} />
                    </label>
                </div>
            </div>
            <div className="flex flex-col justify-center gap-6 bg-[#FAFAFA] p-6 sm:p-8">
                <div>
                    <div className="text-[36px] font-semibold leading-none tracking-[-0.03em] text-[#0A0A0A] tabular-nums sm:text-[44px]">
                        ${yearly.toLocaleString('en-US')}
                    </div>
                    <div className="mt-2 text-[14px] text-[#6B6B6B]">walks out of the tills in a year</div>
                </div>
                <div className="h-px bg-[rgba(10,10,10,0.08)]" />
                <div>
                    <div className="text-[24px] font-semibold leading-none tracking-[-0.02em] text-[#0A0A0A] tabular-nums">
                        {hours.toLocaleString('en-US', { maximumFractionDigits: 0 })} hours
                    </div>
                    <div className="mt-2 text-[14px] text-[#6B6B6B]">spent closing books by hand each year</div>
                </div>
            </div>
        </div>
    );
}

// ── Compact plan card, the front-page version of the pricing page ─────────────
// Tiers, prices and the extra-location rate mirror /pricing exactly. If one
// moves, move the other; two published prices that disagree is worse than none.
const TIERS = [
    { id: 'starter', name: 'Starter', monthly: 149, annual: 124, included: 1, blurb: 'One location getting control of its cash and its day.' },
    { id: 'growth', name: 'Growth', monthly: 399, annual: 332, included: 5, blurb: 'Several locations reconciled together, end to end.', popular: true },
    { id: 'enterprise', name: 'Enterprise', custom: true, included: null, blurb: 'Large estates and bespoke integrations.' },
];
const EXTRA_LOCATION = 49;

function PlanCard() {
    const [tierId, setTierId] = useState('growth');
    const [annual, setAnnual] = useState(false);
    const [locations, setLocations] = useState(5);

    const tier = TIERS.find((t) => t.id === tierId);
    const rate = tier.custom ? null : (annual ? tier.annual : tier.monthly);
    const extra = tier.custom || tier.included == null ? 0 : Math.max(0, locations - tier.included);
    const monthly = tier.custom ? null : rate + extra * EXTRA_LOCATION;

    return (
        <div className="grid grid-cols-1 overflow-hidden rounded-[28px] border border-[rgba(10,10,10,0.10)] bg-white lg:grid-cols-2">
            <div className="border-b border-[rgba(10,10,10,0.08)] p-3 lg:border-b-0 lg:border-r">
                <MediaSlot
                    kind="Image"
                    label="The workspace on a laptop beside an iPad on the counter"
                    note="One clean product shot, neutral surface, the daily close open on screen"
                    ratio="1 / 1"
                    className="!rounded-[24px] h-full"
                />
            </div>
            <div className="flex flex-col gap-6 p-6 sm:p-8">
                <div className="flex items-center justify-between gap-4">
                    <div className="text-[14px] font-medium text-[#0A0A0A]">Billing</div>
                    <div className="flex items-center gap-1 rounded-full border border-[rgba(10,10,10,0.10)] p-1">
                        {[['Monthly', false], ['Annual', true]].map(([label, v]) => (
                            <button
                                key={label}
                                type="button"
                                onClick={() => setAnnual(v)}
                                className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors ${annual === v ? 'bg-[#0A0A0A] text-white' : 'text-[#6B6B6B] hover:text-[#0A0A0A]'}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                    {TIERS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => { setTierId(t.id); if (t.included) setLocations(t.included); }}
                            className={`relative flex flex-col gap-1 rounded-[16px] border p-3 text-left transition-colors ${tierId === t.id ? 'border-[#0A0A0A] bg-[#FAFAFA]' : 'border-[rgba(10,10,10,0.10)] bg-white hover:border-[rgba(10,10,10,0.30)]'}`}
                        >
                            <span className="text-[13px] font-semibold text-[#0A0A0A]">{t.name}</span>
                            <span className="text-[11.5px] leading-[1.35] text-[#A1A1A1]">
                                {t.custom ? 'By conversation' : `${t.included} location${t.included > 1 ? 's' : ''}`}
                            </span>
                        </button>
                    ))}
                </div>

                {!tier.custom && (
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <div className="text-[14px] font-medium text-[#0A0A0A]">Locations</div>
                            <div className="text-[12px] text-[#A1A1A1]">
                                {tier.included} included, then ${EXTRA_LOCATION} each
                            </div>
                        </div>
                        <div className="flex items-center gap-1 rounded-full border border-[rgba(10,10,10,0.10)] p-1">
                            <button type="button" aria-label="Fewer locations" onClick={() => setLocations((c) => Math.max(1, c - 1))} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#6B6B6B] transition-colors hover:bg-[#F5F5F5] hover:text-[#0A0A0A]">
                                <Icon name="minus" className="h-4 w-4" />
                            </button>
                            <span className={`w-8 text-center text-[15px] font-semibold text-[#0A0A0A] ${NUMS}`}>{locations}</span>
                            <button type="button" aria-label="More locations" onClick={() => setLocations((c) => Math.min(60, c + 1))} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#6B6B6B] transition-colors hover:bg-[#F5F5F5] hover:text-[#0A0A0A]">
                                <Icon name="plus" className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-2.5">
                    {[
                        ['This tier', tier.blurb],
                        ['Companion apps', 'Register, scale, packing, receiving and delivery, included'],
                        ['Setup', 'Done with you, including your first close'],
                    ].map(([k, v]) => (
                        <div key={k} className="flex items-baseline justify-between gap-4 border-b border-[rgba(10,10,10,0.06)] pb-2.5 last:border-0">
                            <span className="text-[13px] text-[#6B6B6B]">{k}</span>
                            <span className="max-w-[62%] text-right text-[13px] font-medium text-[#0A0A0A]">{v}</span>
                        </div>
                    ))}
                </div>

                <div className="mt-auto flex items-end justify-between gap-4">
                    <div>
                        <div className="text-[12px] text-[#A1A1A1]">{tier.custom ? 'Priced with you' : extra > 0 ? `Including ${extra} extra` : 'From'}</div>
                        <div className={`text-[32px] font-semibold leading-none tracking-[-0.03em] text-[#0A0A0A] ${NUMS}`}>
                            {tier.custom ? 'Talk to us' : `$${monthly.toLocaleString('en-US')}`}
                        </div>
                        <div className="text-[12px] text-[#A1A1A1]">
                            {tier.custom ? 'Scoped on a call' : annual ? 'per month, billed annually' : 'per month, billed monthly'}
                        </div>
                    </div>
                    <Link to={tier.custom ? '/contact' : '/signup'} className={BTN_MD}>
                        {tier.custom ? 'Talk to us' : 'Start free'}
                        <Icon name="arrow" className="h-4 w-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function LandingPage() {
    return (
        <MarketingShell hero>
            {/* `/` is the canonical home. /overview renders this same page, and
                scripts/prerender.js points its static copy here, so the client has
                to declare the same canonical or the two would disagree. */}
            <Seo
                title="Back office and reconciliation for multi-location retail"
                description="Wavelength is the back office for operators running more than one location: close the day, prove the cash with blind counts, reconcile takings to the bank."
                path="/"
                keywords="multi location retail back office, restaurant back office software, cash reconciliation software, daily close software, bank reconciliation for retail, POS reporting across locations, grocery store back office, Toast back office alternative, Clover reporting, Square multi location reporting, food wholesale invoicing software"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'Organization',
                            '@id': 'https://wavelengtherp.com/#organization',
                            name: 'Wavelength ERP',
                            url: 'https://wavelengtherp.com',
                            description: 'Back-office and reconciliation software for multi-location retail and food operators.',
                        },
                        {
                            '@type': 'SoftwareApplication',
                            name: 'Wavelength',
                            applicationCategory: 'BusinessApplication',
                            applicationSubCategory: 'Retail back office and reconciliation',
                            operatingSystem: 'Web, iOS, iPadOS',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                            offers: {
                                '@type': 'AggregateOffer',
                                lowPrice: '124',
                                highPrice: '399',
                                priceCurrency: 'USD',
                                offerCount: 3,
                                url: 'https://wavelengtherp.com/pricing',
                            },
                        },
                        {
                            '@type': 'FAQPage',
                            mainEntity: FAQ.map((f) => ({
                                '@type': 'Question',
                                name: f.q,
                                acceptedAnswer: { '@type': 'Answer', text: f.a },
                            })),
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <HeroPanel
                active="home"
                announcement="Card, cash and bank reconciled in one pass"
                titleTop="Every dollar."
                titleBottom="Every store, every day."
                body={<>Wavelength is the <KeyDark>back office</KeyDark> for operators running more than one location. It closes the day, proves the cash, and reconciles what you sold against <KeyDark>what the bank actually received</KeyDark>.</>}
                primary={{ label: 'Start free', to: '/signup' }}
                secondary={{ label: 'See how it works', to: '/how-it-works' }}
                mediaLabel="Hero video: a manager closing the day on an iPad at the counter after hours"
                mediaNote="Quiet, wide, low light. The screen is the only bright thing in the frame."
                footNote="Keep the register you already run. We reconcile it."
            />

            {/* ── The band under the hero ─────────────────────────────────
                A normal section, not a slab tucked under the panel. The hero is
                a self-contained rounded panel and its bottom corners have to stay
                visible, so nothing may overlap it. */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <p className="mx-auto max-w-[34ch] text-balance text-center text-[22px] font-semibold leading-[1.2] tracking-[-0.01em] text-[#0A0A0A] sm:text-[28px]">
                        Your register tells you what you rang. Wavelength tells you whether the bank got it.
                    </p>
                    <div className="mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
                        {POS_LOGOS.map((l) => (
                            <span key={l} className="text-[17px] font-medium tracking-[-0.01em] text-[#A1A1A1] opacity-80">{l}</span>
                        ))}
                    </div>
                    <p className="mt-6 text-center text-[13px] text-[#A1A1A1]">
                        Sits alongside whatever each store runs. Sources are added on request.
                    </p>
                </Container>
            </Section>

            {/* ── How it works ───────────────────────────────────────────── */}
            <Section id="how" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="How it works."
                            lead="Connect the systems you already run, close each day where it happened, then let the numbers meet the bank. Three steps, and the third one stops being your evening."
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <MediaCardGrid
                            numbered
                            className="mt-16"
                            items={[
                                {
                                    title: 'Connect',
                                    body: 'Point Wavelength at your registers, your online store and your bank. Locations, staff and products come across with their history.',
                                    mediaLabel: 'Connection screen with a store being added',
                                    mediaNote: 'Phone or laptop screenshot',
                                    ratio: '4 / 5',
                                },
                                {
                                    title: 'Close',
                                    body: 'At the end of trade, the manager counts blind, records the deposit and signs off. The day locks and cannot be quietly edited later.',
                                    mediaLabel: 'Daily close screen mid count',
                                    mediaNote: 'iPad screenshot, held in one hand',
                                    ratio: '4 / 5',
                                },
                                {
                                    title: 'Reconcile',
                                    body: 'Card, cash and bank are matched against each other overnight. In the morning you read a list of what did not tie, not a pile of paper.',
                                    mediaLabel: 'Reconciliation screen with the exceptions list',
                                    mediaNote: 'Laptop screenshot',
                                    ratio: '4 / 5',
                                },
                            ]}
                        />
                    </Reveal>
                    <Reveal delay={140}>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                            <Pill active>Daily close</Pill>
                            <Pill>Blind counts</Pill>
                            <Pill>Bank matching</Pill>
                            <Pill>Processor payouts</Pill>
                            <Pill>QuickBooks</Pill>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Dark panel: everything in one workspace ─────────────────── */}
            <Section pad="sm">
                <Container>
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] bg-[#111111] p-6 text-white sm:p-12 lg:p-14">
                            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px] lg:gap-14">
                                <div className="flex flex-col">
                                    <h2 className="max-w-[420px] text-[28px] font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-[36px]">
                                        Everything lives in one workspace.
                                    </h2>
                                    <div className="mt-7 flex flex-wrap gap-2">
                                        {['Sales', 'Daily close', 'Cash control', 'Bank statements', 'Payment mix', 'Inventory', 'Purchasing', 'Receiving', 'Production', 'Labels', 'Wholesale', 'Customers', 'Campaigns', 'Devices', 'Staff', 'Audit log'].map((c, i) => (
                                            <Pill key={c} dark active={i === 0}>{c}</Pill>
                                        ))}
                                    </div>
                                    <div className="mt-auto max-w-[440px] pt-12">
                                        <h3 className="text-[19px] font-semibold tracking-[-0.015em] text-white sm:text-[22px]">
                                            One login. Every location
                                        </h3>
                                        <p className="mt-2.5 text-[14px] leading-[1.65] text-white/55">
                                            Switch stores from the top of the screen and the whole workspace follows: the same day, the same tenders, the same rules. Nothing is stitched together in a spreadsheet at the end of the week.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <MediaSlot
                                        dark
                                        kind="Image"
                                        label="The workspace on a tablet"
                                        note="Straight-on screenshot of the dashboard, dark room"
                                        ratio="3 / 4"
                                        className="!rounded-[28px] max-w-[340px]"
                                    />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── The nine modules ───────────────────────────────────────── */}
            <Section id="platform" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Nine jobs, one system."
                            lead="Each of these is a product on its own somewhere else, bought separately and reconciled by hand. Here they share one set of numbers."
                            actions={<Link to="/features" className={BTN_SECONDARY}>Everything in the platform<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={MODULES} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Feature story 1: reconciliation ─────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Reconciliation"
                            title="Three sources. One answer."
                            body="What the registers rang, what the processor paid out and what the bank received are three different numbers until something makes them agree. Wavelength matches all three every night and hands you only the difference."
                            bullets={[
                                'Bank statements imported and matched against sales and payouts',
                                'Processor deposits traced back to the batch and the store',
                                'Unconfirmed money aged, so nothing sits quietly for a month',
                                'Everything unmatched lands in one exceptions queue',
                            ]}
                            cta={<Link to="/features#reconciliation" className={BTN_SECONDARY}>See reconciliation<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Reconciliation screen, three columns matching"
                                    note="Sales, processor payouts and bank deposits side by side with the unmatched rows highlighted"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Feature story 2: cash accountability ────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            reverse
                            eyebrow="Cash accountability"
                            title="Count blind. Sign your name."
                            body="A drawer that shows the expected total is not a count, it is a confirmation. Wavelength hides the figure until the money is counted, then records who counted it, on which drawer, at what time."
                            bullets={[
                                'Blind drawer and safe counts, with the variance attributed by person',
                                'Deposit slips recorded and matched to what actually reached the bank',
                                'Over and short trended by store, by shift and by employee',
                                'The day locks on sign-off, and every later change is on the audit log',
                            ]}
                            cta={<Link to="/features#cash" className={BTN_SECONDARY}>See cash control<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Blind count screen mid entry"
                                    note="iPad held at the safe, denominations being keyed, expected total hidden"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Feature story 3: inventory and production ───────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Inventory and production"
                            title="On hand, because it moved."
                            body="Stock is not a number somebody typed. It is the sum of everything received, produced, sold, wasted and counted, kept as an append-only ledger you can read back line by line."
                            bullets={[
                                'Every movement dated, sourced and reversible only by another movement',
                                'Reorder boards that turn low stock into a purchase order',
                                'Receiving signed off on an iPad at the door, with temperatures and variances',
                                'Raw material to finished pack, with yield and waste on the record',
                            ]}
                            cta={<Link to="/features#inventory" className={BTN_SECONDARY}>See inventory<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Photo"
                                    label="Receiving at the back door on an iPad"
                                    note="Hands, a case of product, the app open on the delivery"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Feature story 4: selling ────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            reverse
                            eyebrow="Sales and customers"
                            title="What sold, where, and to whom."
                            body="Counter sales and online orders in the same tables, with the same statuses and the same tenders, so the answer to any question is one number rather than two that disagree."
                            bullets={[
                                'Revenue by location, product, hour, staff member and channel',
                                'Customers, repeat rate, average order value and lifetime value',
                                'Campaign spend matched to the orders and revenue it produced',
                                'Holiday and seasonal lift measured against the same weekday last year',
                            ]}
                            cta={<Link to="/features#sales" className={BTN_SECONDARY}>See reporting<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Sales dashboard with the trend and the leaderboard"
                                    note="Laptop screenshot, one chart and a ranked table"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── The apps ───────────────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="The floor gets apps, not paperwork."
                            lead="Five companion apps feed the same workspace, so what happens on the floor is already in the numbers before anyone opens a laptop."
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {[
                                { title: 'Register', body: 'Takes the order, the weight and the payment at the counter, and keeps working when the internet does not.', shot: 'The register app open at the counter, an order part rung', note: 'iPad held portrait on a stand, order lines on the left, tender pad on the right' },
                                { title: 'Scale', body: 'Weighs, prices and prints the label in your own format, straight into the production record.', shot: 'The scale app on the production bench, a weight settling', note: 'iPad beside the platter, price per pound and total on screen, printer at the edge of frame' },
                                { title: 'Receiving', body: 'Signs off the delivery at the door with temperatures, variances and a report the office can read.', shot: 'The receiving app at the back door, a temperature being keyed', note: 'iPad held in one hand, pallet out of focus behind it, the 41F check on screen' },
                                { title: 'Packing', body: 'Locks the order to the person packing it, so two people never grab the same one, and marks it done when it leaves.', shot: 'The packing app with one order locked to a packer', note: 'iPad flat on a packing bench, the order list showing who holds which order' },
                                { title: 'Delivery', body: 'Puts the driver on the map while the run is out, and shows the customer where their order is.', shot: 'The delivery app mid run, the map following the van', note: 'iPhone in a windscreen mount, route and next stop on screen, daylight' },
                            ].map((a) => (
                                <div key={a.title} className="flex flex-col overflow-hidden rounded-[28px] border border-[rgba(10,10,10,0.10)] bg-white">
                                    <div className="p-2.5">
                                        <MediaSlot
                                            kind="Screenshot"
                                            label={a.shot}
                                            note={a.note}
                                            ratio="3 / 4"
                                            className="!rounded-[24px]"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2 px-5 pb-6 pt-2">
                                        <div className="text-[16px] font-semibold tracking-[-0.01em] text-[#0A0A0A]">{a.title}</div>
                                        <Body className="text-[13.5px]">{a.body}</Body>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Spec panel ─────────────────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#F0F0F0] via-[#F7F7F7] to-[#E9E9E9] p-6 sm:p-12 lg:p-14">
                            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
                                <div className="flex flex-col gap-5">
                                    <Heading className="max-w-[380px]">One system. Built to survive a real trading day.</Heading>
                                    <Lead className="max-w-[400px]">
                                        Offline-tolerant on the floor, append-only in the ledger, role-scoped for every manager, and audited on every change. Nothing here assumes a quiet afternoon.
                                    </Lead>
                                    <div className="mt-1">
                                        <Link to="/security" className={BTN_PRIMARY}>How it is built<Icon name="arrow" className="h-4 w-4" /></Link>
                                    </div>
                                </div>
                                <div className="relative">
                                    <MediaSlot
                                        kind="Photo"
                                        label="The device family on a counter"
                                        note="Register, scale and iPad together, top-down, neutral surface"
                                        ratio="1 / 1"
                                        className="!rounded-[28px] !bg-[#E4E4E4]"
                                    />
                                    <div className="pointer-events-none absolute inset-0 hidden sm:block">
                                        <FloatingChip className="absolute left-4 top-6">Works offline</FloatingChip>
                                        <FloatingChip className="absolute right-4 top-1/3">Audit logged</FloatingChip>
                                        <FloatingChip className="absolute bottom-8 left-8">Role scoped</FloatingChip>
                                        <FloatingChip className="absolute bottom-20 right-6">Multi location</FloatingChip>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Stats + calculator ─────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Your back office is still running on paper."
                            lead="Most multi-location operators close the day in a notebook, reconcile the bank in a spreadsheet, and find the shortfall a month later. None of that is a software problem until you count what it costs."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <StatBand
                            className="mt-12"
                            countUp
                            stats={[
                                { value: '16', label: 'Modules in one workspace', note: 'From daily close to wholesale receivables' },
                                { value: '5', label: 'Companion apps', note: 'Register, scale, receiving, packing and delivery' },
                                { value: '3', label: 'Sources reconciled', note: 'Register, processor and bank' },
                                { value: '1', label: 'Login for every location', note: 'Switch stores from the top of the screen' },
                            ]}
                        />
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-4">
                            <ExposureCalculator />
                        </div>
                    </Reveal>
                    <Reveal delay={160}>
                        <div className="mt-4 flex flex-col gap-4 rounded-[24px] bg-[#111111] px-6 py-7 text-white sm:flex-row sm:items-center sm:justify-between sm:px-8">
                            <div>
                                <div className="text-[20px] font-semibold tracking-[-0.015em] sm:text-[22px]">Take the evening back.</div>
                                <p className="mt-1.5 text-[14px] leading-[1.55] text-white/55">Close one day in Wavelength and see the difference in the morning.</p>
                            </div>
                            <div className="flex flex-none flex-wrap gap-2">
                                <Link to="/signup" className={BTN_ON_DARK}>Start free</Link>
                                <Link to="/contact" className={BTN_ON_DARK_SOFT}>Book a walkthrough</Link>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── What it replaces ───────────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="What it replaces."
                            lead="Not the register. The pile of things around the register that nobody sells as a product."
                            maxWidth="max-w-[480px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12 grid grid-cols-1 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white sm:grid-cols-3">
                            {[
                                { t: 'The close-out notebook', b: 'Register totals, drawer counts and the deposit written on paper, then keyed into a spreadsheet a week later by somebody who was not there.' },
                                { t: 'The reconciliation spreadsheet', b: 'A tab per month, formulas nobody trusts, and a difference at the bottom that gets rounded away because chasing it costs more than it is worth.' },
                                { t: 'The four separate logins', b: 'One for the register reports, one for the online store, one for the processor and one for the bank, with no shared idea of a day.' },
                            ].map((x, i) => (
                                <div key={x.t} className={`flex flex-col gap-3 p-7 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)] sm:border-l sm:border-t-0' : ''}`}>
                                    <span className="text-[13px] font-medium tabular-nums text-[#A3A3A3]">{String(i + 1).padStart(2, '0')}</span>
                                    <div className="text-[17px] font-semibold tracking-[-0.015em] text-[#0A0A0A]">{x.t}</div>
                                    <Body>{x.b}</Body>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Everything else ────────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="And the parts nobody advertises."
                            lead="The unglamorous half of running several locations, built in rather than bolted on."
                            maxWidth="max-w-[500px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={EXTRAS} cols={4} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Steps ──────────────────────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[380px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Heading>Live in a week, not a quarter.</Heading>
                                <Lead>No rip and replace, no freeze on trading, no consultant on site for a month.</Lead>
                                <div className="mt-2">
                                    <Link to="/contact" className={BTN_PRIMARY}>Book a walkthrough<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <Steps
                                items={[
                                    { title: 'Connect one store', body: 'Point Wavelength at the register and the online store. Products, staff and order history come across on their own.' },
                                    { title: 'Set who sees what', body: 'Pick your locations and apply a role preset per person. Managers get their own store, the office gets everything.' },
                                    { title: 'Close a day together', body: 'We run the first close with you: the blind count, the deposit, the sign-off and the lock.' },
                                    { title: 'Import a bank statement', body: 'Upload a month of bank activity and watch it match against sales and payouts. What is left is your real exception list.' },
                                    { title: 'Add the rest', body: 'Turn on the remaining locations, the scales, receiving and wholesale as you are ready. Nothing has to land at once.' },
                                ]}
                            />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Plans ──────────────────────────────────────────────────── */}
            <Section id="pricing" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Pricing you can read without a call."
                            lead="Three tiers, published. The companion apps, the registers and your staff accounts are not charged separately, because charging for them would only teach you to use fewer."
                            actions={<Link to="/pricing" className={BTN_SECONDARY}>Full pricing<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12">
                            <PlanCard />
                        </div>
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                            {['Published pricing', 'No per-register charge', 'Apps included', 'Setup done with you', 'Your data exports'].map((t) => (
                                <Badge key={t}>{t}</Badge>
                            ))}
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── FAQ ────────────────────────────────────────────────────── */}
            <Section id="faq" pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Heading>Questions, answered.</Heading>
                                <Lead>If yours is not here, ask us directly and a person will answer.</Lead>
                                <div className="mt-2">
                                    <Link to="/faq" className={BTN_SECONDARY}>All questions<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <Accordion items={FAQ} />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Get started"
                title="Close tonight. Reconcile by morning."
                body="Start with one location and one day. If it does not find something in the first week, you have lost an evening."
                primary={{ label: 'Start free', to: '/signup' }}
                secondary={{ label: 'Talk to us', to: '/contact' }}
            />
        </MarketingShell>
    );
}
